/**
 * PyramidWidget — Heinrich Safety Triangle. Renders the classic severity
 * triangle (apex = most severe lagging outcome, base = leading indicators) as a
 * true triangle divided into horizontal bands, with a red→green gradient,
 * per-band counts, "Lagging Indicators" / "Leading Indicators" side brackets,
 * and click-to-drill into each band's records. Ratio insights (near-miss :
 * recordable, base : apex) support proactive risk management.
 *
 * `tiers`: [{ metric, label, color, textColor?, group: 'lagging'|'leading' }]
 * ordered apex→base. Counts come from /v2/kpis via the shared DashboardContext.
 */
import React, { useState } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { useKpis } from '../hooks/useDashboardData';
import { isSiteRequiredError, SiteRequiredNotice } from '../utils/siteScope';
import DrilldownModal from './DrilldownModal';
import { CARD, HEADING } from '../ui';

// SVG canvas geometry (viewBox units).
const VB_W = 900;
const VB_H = 560;
const APEX_X = 450;
const TOP = 30;
const BOTTOM = 520;
const BASE_HALF = 300;
const H = BOTTOM - TOP;

const num = (v) => Number(v ?? 0) || 0;
const ratio = (a, b) => (b > 0 ? `${(a / b).toFixed(1)} : 1` : '—');
const halfW = (y) => ((y - TOP) / H) * BASE_HALF;

// Square bracket path + rotated caption, drawn beside the triangle.
function SideBracket({ side, y0, y1, label }) {
  const left = side === 'left';
  const x = left ? 118 : VB_W - 118;
  const tick = left ? 16 : -16; // horizontal end-ticks point toward the triangle
  const mid = (y0 + y1) / 2;
  const labelX = left ? 44 : VB_W - 44;
  return (
    <g stroke="#9ca3af" fill="none" strokeWidth="2">
      <path d={`M ${x + tick} ${y0} H ${x} V ${y1} H ${x + tick}`} />
      <text
        x={labelX}
        y={mid}
        transform={`rotate(-90 ${labelX} ${mid})`}
        textAnchor="middle"
        fontSize="16"
        fontWeight="700"
        fill="#6b7280"
        stroke="none"
      >
        {label}
      </text>
    </g>
  );
}

export default function PyramidWidget({ title = 'Heinrich Safety Triangle', tiers = [] }) {
  const { module, siteId, time, departmentId, dimensions, fyStartMonth, compare, refreshMs } = useDashboard();
  const metricKeys = tiers.map((t) => t.metric);
  const { metrics: values, loading, error } = useKpis({
    module, metrics: metricKeys, siteId, time, departmentId, dimensions, fyStartMonth, compare, refreshMs,
  });
  const [drill, setDrill] = useState(null);
  const [hover, setHover] = useState(null);

  const n = tiers.length || 1;
  const bandY = (i) => TOP + (i / n) * H;

  const get = (k) => num(values[k]?.value);
  const recordable = get('safety.incidents.fatality') + get('safety.incidents.lti') + get('safety.incidents.mtc');
  const nearMiss = get('safety.near_miss.total');
  const apex = get('safety.incidents.fatality') + get('safety.incidents.lti');
  const base = get('safety.observations.unsafe_acts') + get('safety.observations.unsafe_conditions') + nearMiss;

  // Contiguous index ranges per indicator group (for the side brackets).
  const idxOf = (grp) => tiers.reduce((acc, t, i) => (t.group === grp ? [...acc, i] : acc), []);
  const lag = idxOf('lagging');
  const lead = idxOf('leading');

  // The worst place to print an unknown as a zero. The widget ignored `error`
  // entirely, so a failed read drew a full triangle reading 0 fatalities,
  // 0 lost-time injuries — a safety claim, made out of a network response.
  if (isSiteRequiredError(error)) {
    return (
      <div className={`${CARD} p-4`}>
        <h3 className={`${HEADING} mb-3`}>{title}</h3>
        <SiteRequiredNotice />
      </div>
    );
  }

  return (
    <div className={`${CARD} p-4`}>
      <h3 className={`${HEADING} mb-3`}>{title}</h3>

      {loading ? (
        <div className="h-72 animate-pulse rounded bg-gray-100" />
      ) : (
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          width="100%"
          role="img"
          aria-label={title}
          style={{ maxWidth: 720, margin: '0 auto', display: 'block' }}
        >
          {lag.length > 0 && (
            <SideBracket side="left" y0={bandY(lag[0])} y1={bandY(lag[lag.length - 1] + 1)} label="Lagging Indicators" />
          )}
          {lead.length > 0 && (
            <SideBracket side="right" y0={bandY(lead[0])} y1={bandY(lead[lead.length - 1] + 1)} label="Leading Indicators" />
          )}

          {tiers.map((t, i) => {
            const y0 = bandY(i);
            const y1 = bandY(i + 1);
            const pts = [
              [APEX_X - halfW(y0), y0], [APEX_X + halfW(y0), y0],
              [APEX_X + halfW(y1), y1], [APEX_X - halfW(y1), y1],
            ].map((p) => p.join(',')).join(' ');
            const cy = (y0 + y1) / 2;
            const value = values[t.metric]?.value ?? 0;
            const ink = t.textColor || '#1f2937';
            const dim = hover !== null && hover !== i;
            return (
              <g
                key={t.metric || i}
                onClick={t.metric ? () => setDrill({ metric: t.metric, label: t.label }) : undefined}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: t.metric ? 'pointer' : 'default' }}
              >
                <polygon
                  points={pts}
                  fill={t.color}
                  stroke="#ffffff"
                  strokeWidth="2"
                  style={{ opacity: dim ? 0.72 : 1, transition: 'opacity 120ms' }}
                />
                <text x={APEX_X} y={cy - 3} textAnchor="middle" fontSize="20" fontWeight="700" fill={ink}>
                  {value}
                </text>
                <text x={APEX_X} y={cy + 16} textAnchor="middle" fontSize="12.5" fill={ink}>
                  {t.label}
                </text>
              </g>
            );
          })}
        </svg>
      )}

      {!loading && (
        <div className="mt-4 grid grid-cols-3 gap-3 border-t border-gray-100 pt-3 text-center text-xs">
          <div>
            <div className="text-gray-400">Near-miss : recordable</div>
            <div className="font-semibold text-gray-800">{ratio(nearMiss, recordable)}</div>
          </div>
          <div>
            <div className="text-gray-400">Base : apex</div>
            <div className="font-semibold text-gray-800">{ratio(base, apex)}</div>
          </div>
          <div>
            <div className="text-gray-400">Leading : lagging</div>
            <div className="font-semibold text-gray-800">{ratio(base, recordable)}</div>
          </div>
        </div>
      )}

      <DrilldownModal open={!!drill} metric={drill?.metric} title={drill?.label} onClose={() => setDrill(null)} />
    </div>
  );
}
